
import { Vue, Component, Prop } from 'vue-property-decorator';
import moment from 'moment';
import {
  RuleAction, RuleActionDefinition, RuleCustomValue, RuleEmailDelivery, RuleExecution, RuleLogAction, RuleRunInfo,
} from '@/types';
import { hydrateAction, isCustomValueDisplayable } from '@/helpers/ruleHelpers';
import { deliveryLabel, notEvaluatedCount } from '@/helpers/ruleLogHelpers';
import RuleCustomValueDisplay from './RuleCustomValueDisplay.vue';
import RuleLogConditionTree from './RuleLogConditionTree.vue';

@Component({ components: { RuleCustomValueDisplay, RuleLogConditionTree } })
export default class RuleExecutionDetails extends Vue {
  @Prop({ type: Object, required: true }) readonly execution!: RuleExecution;

  get run(): RuleRunInfo | null {
    return this.execution.run;
  }

  get actions(): RuleLogAction[] {
    return this.execution.actions || [];
  }

  get deliveries(): RuleEmailDelivery[] {
    return this.execution.deliveries || [];
  }

  get actionDefinitions(): RuleActionDefinition[] {
    return this.$store.getters.ruleActionDefinitions || [];
  }

  get notEvaluated(): number {
    return notEvaluatedCount(this.execution);
  }

  get notEvaluatedText(): string {
    const n = this.notEvaluated;
    const what = n === 1 ? 'Un\'altra condizione non è stata valutata' : `Altre ${n} condizioni non sono state valutate`;
    if (this.execution.outcome === 'triggered') return `${what}: la regola era già scattata.`;
    if (this.execution.outcome === 'error') return `${what}: la valutazione si è interrotta per l'errore.`;
    return `${what}: la regola si è fermata prima.`;
  }

  get delaySeconds(): number | null {
    if (!this.run || !this.run.queuedAt || !this.run.startedAt) return null;
    const ms = new Date(this.run.startedAt).getTime() - new Date(this.run.queuedAt).getTime();
    return Math.max(0, Math.round(ms / 1000));
  }

  time(value: string): string {
    return moment(value).format('DD/MM/YYYY [alle] HH:mm:ss');
  }

  hydrated(action: RuleLogAction): RuleAction | null {
    return hydrateAction({
      codename: action.codename, name: '', order: 0, customValues: action.customValues || [],
    }, this.actionDefinitions);
  }

  actionName(action: RuleLogAction): string {
    const h = this.hydrated(action);
    return h ? h.name : action.codename;
  }

  actionValues(action: RuleLogAction): RuleCustomValue[] {
    const h = this.hydrated(action);
    return h ? h.customValues.filter(isCustomValueDisplayable) : [];
  }

  actionStatusText(action: RuleLogAction): string {
    if (action.status === 'error') return `errore: ${action.error}`;
    if (action.status === 'skipped') return `saltata: ${action.reason}`;
    return 'eseguita';
  }

  actionIcon(action: RuleLogAction): string {
    if (action.status === 'error') return 'mdi-alert-circle';
    if (action.status === 'skipped') return 'mdi-debug-step-over';
    return 'mdi-check-circle';
  }

  actionColor(action: RuleLogAction): string {
    if (action.status === 'error') return 'error';
    if (action.status === 'skipped') return 'grey';
    return 'statusDone';
  }

  deliveriesFor(index: number): RuleEmailDelivery[] {
    return this.deliveries.filter((d) => d.actionIndex === index);
  }

  sentRows(index: number): RuleEmailDelivery[] {
    return this.deliveriesFor(index).filter((d) => d.status !== 'excluded');
  }

  deliveryText(d: RuleEmailDelivery): string {
    return deliveryLabel(d);
  }

  deliveryColor(d: RuleEmailDelivery): string {
    switch (d.status) {
      case 'sent': return 'statusDone';
      case 'failed': return 'error';
      case 'excluded': return 'grey';
      case 'log_only': return 'info';
      default: return 'warning';
    }
  }
}
